import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

/**
 * Device group data structure
 */
export interface DeviceGroup {
  id: string;
  name: string;
  description: string | null;
  color: string | null;
  fogGroupId: number | null;
  syncToFog: boolean;
  linkedToFog: boolean;
  deviceCount: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * FOG group available for import
 */
export interface FogGroupInfo {
  id: number;
  name: string;
  description: string | null;
  hostCount: number;
}

/**
 * Device in a group (with membership info)
 */
export interface GroupDevice {
  membershipId: string;
  deviceId: string;
  hostname: string;
  ipAddress: string | null;
  location: string | null;
  deviceType: string;
  isActive: boolean;
  lastSeenAt: string | null;
  site: string | null;
  addedBy: string;
  addedAt: string;
  notes: string | null;
}

/**
 * Create/update group request
 */
export interface DeviceGroupRequest {
  name: string;
  description?: string | null;
  color?: string | null;
}

/**
 * Add devices to group request
 */
export interface AddDevicesRequest {
  groupId: string;
  deviceIds: string[];
  notes?: string;
}

/**
 * Remove devices from group request
 */
export interface RemoveDevicesRequest {
  groupId: string;
  deviceIds: string[];
}

// Extend queryKeys with device groups
const deviceGroupKeys = {
  all: ['deviceGroups'] as const,
  lists: () => [...deviceGroupKeys.all, 'list'] as const,
  list: () => [...deviceGroupKeys.lists()] as const,
  details: () => [...deviceGroupKeys.all, 'detail'] as const,
  detail: (id: string) => [...deviceGroupKeys.details(), id] as const,
  devices: (id: string) => [...deviceGroupKeys.all, 'devices', id] as const,
  fogAvailable: () => [...deviceGroupKeys.all, 'fogAvailable'] as const,
};

/**
 * Hook to fetch all device groups
 */
export function useDeviceGroups() {
  return useQuery({
    queryKey: deviceGroupKeys.list(),
    queryFn: async () => {
      const response = await api.get<{ groups: DeviceGroup[]; total: number }>(
        '/api/v1/device-groups'
      );
      return response;
    },
  });
}

/**
 * Hook to fetch a single device group
 */
export function useDeviceGroup(id: string | null) {
  return useQuery({
    queryKey: deviceGroupKeys.detail(id ?? ''),
    queryFn: async () => {
      const response = await api.get<{ group: DeviceGroup }>(
        `/api/v1/device-groups/${id}`
      );
      return response.group;
    },
    enabled: !!id,
  });
}

/**
 * Hook to fetch devices in a group
 */
export function useGroupDevices(groupId: string | null) {
  return useQuery({
    queryKey: deviceGroupKeys.devices(groupId ?? ''),
    queryFn: async () => {
      const response = await api.get<{
        group: DeviceGroup;
        devices: GroupDevice[];
        total: number;
      }>(`/api/v1/device-groups/${groupId}/devices`);
      return response;
    },
    enabled: !!groupId,
  });
}

/**
 * Hook to create a device group
 */
export function useCreateDeviceGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: DeviceGroupRequest) => {
      const response = await api.post<{ success: boolean; group: DeviceGroup }>(
        '/api/v1/device-groups',
        data
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceGroupKeys.all });
    },
  });
}

/**
 * Hook to update a device group
 */
export function useUpdateDeviceGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<DeviceGroupRequest> }) => {
      const response = await api.put<{ success: boolean; group: DeviceGroup }>(
        `/api/v1/device-groups/${id}`,
        data
      );
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: deviceGroupKeys.all });
      queryClient.invalidateQueries({ queryKey: deviceGroupKeys.detail(variables.id) });
    },
  });
}

/**
 * Hook to delete a device group
 */
export function useDeleteDeviceGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete<{ success: boolean; message: string }>(
        `/api/v1/device-groups/${id}`
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceGroupKeys.all });
    },
  });
}

/**
 * Hook to add devices to a group
 */
export function useAddDevicesToGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, deviceIds, notes }: AddDevicesRequest) => {
      const response = await api.post<{
        success: boolean;
        added: number;
        skipped: number;
        notFound: number;
        message: string;
      }>(`/api/v1/device-groups/${groupId}/devices`, { deviceIds, notes });
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: deviceGroupKeys.all });
      queryClient.invalidateQueries({ queryKey: deviceGroupKeys.devices(variables.groupId) });
    },
  });
}

/**
 * Hook to remove devices from a group
 */
export function useRemoveDevicesFromGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, deviceIds }: RemoveDevicesRequest) => {
      const response = await api.delete<{
        success: boolean;
        removed: number;
        message: string;
      }>(`/api/v1/device-groups/${groupId}/devices`, {
        body: JSON.stringify({ deviceIds }),
      });
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: deviceGroupKeys.all });
      queryClient.invalidateQueries({ queryKey: deviceGroupKeys.devices(variables.groupId) });
    },
  });
}

// ========== FOG Integration Hooks ==========

/**
 * Hook to fetch FOG groups available for import
 */
export function useFogGroupsAvailable() {
  return useQuery({
    queryKey: deviceGroupKeys.fogAvailable(),
    queryFn: async () => {
      const response = await api.get<{ fogGroups: FogGroupInfo[]; total: number }>(
        '/api/v1/device-groups/fog/available'
      );
      return response;
    },
  });
}

/**
 * Hook to import all FOG groups
 */
export function useImportFogGroups() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await api.post<{
        success: boolean;
        imported: number;
        skipped: number;
        errors: string[];
        message: string;
      }>('/api/v1/device-groups/fog/import', {});
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceGroupKeys.all });
    },
  });
}

/**
 * Hook to sync a group to FOG
 */
export function useSyncGroupToFog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupId: string) => {
      const response = await api.post<{
        success: boolean;
        group: DeviceGroup;
        fogGroup: { id: number; name: string };
        message: string;
      }>(`/api/v1/device-groups/${groupId}/sync-to-fog`, {});
      return response;
    },
    onSuccess: (_, groupId) => {
      queryClient.invalidateQueries({ queryKey: deviceGroupKeys.all });
      queryClient.invalidateQueries({ queryKey: deviceGroupKeys.detail(groupId) });
    },
  });
}

/**
 * Hook to link a group to an existing FOG group
 */
export function useLinkGroupToFog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, fogGroupId }: { groupId: string; fogGroupId: number }) => {
      const response = await api.post<{
        success: boolean;
        group: DeviceGroup;
        fogGroup: { id: number; name: string };
        message: string;
      }>(`/api/v1/device-groups/${groupId}/link-fog`, { fogGroupId });
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: deviceGroupKeys.all });
      queryClient.invalidateQueries({ queryKey: deviceGroupKeys.detail(variables.groupId) });
      queryClient.invalidateQueries({ queryKey: deviceGroupKeys.fogAvailable() });
    },
  });
}

/**
 * Hook to unlink a group from FOG
 */
export function useUnlinkGroupFromFog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupId: string) => {
      const response = await api.post<{
        success: boolean;
        group: DeviceGroup;
        message: string;
      }>(`/api/v1/device-groups/${groupId}/unlink-fog`, {});
      return response;
    },
    onSuccess: (_, groupId) => {
      queryClient.invalidateQueries({ queryKey: deviceGroupKeys.all });
      queryClient.invalidateQueries({ queryKey: deviceGroupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: deviceGroupKeys.fogAvailable() });
    },
  });
}
